/**
 * EditUserModal Component - Modal for editing existing users
 */
import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { mascaraTelefone } from '../../lib/utils';
import { ROLE_OPTIONS } from '../../constants/roles';
import SMTPConfigSection, { SMTPConfig } from '../SMTPConfigSection';
import { validatePassword, getStrengthColor } from '../../services/passwordService';
import { User } from '../../types/user';
import useAuthStore from '../../stores/authStore';

interface EditUserModalProps {
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
  onSave: (user: User, smtpConfig: SMTPConfig) => Promise<boolean>;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  user,
  onClose,
  onSave,
}) => {
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [fullName, setFullName] = useState('');
  // N116: mesmo padrão do UsersPage (useAuthStore((s) => s.user)). Visibilidade
  // é conveniência; o enforcement é do backend, que exige admin no /users.
  const isMaster = useAuthStore((s) => s.user)?.role === 'MASTER';
  const [smtpConfig, setSmtpConfig] = useState<SMTPConfig>({
    smtp_email: '',
    smtp_senha: '',
    smtp_servidor: 'smtp.gmail.com',
    smtp_porta: 587,
    smtp_tls: true,
  });
  const [saving, setSaving] = useState(false);

  // Password strength state
  const [passwordStrength, setPasswordStrength] = useState<{ score: number; label: string }>({ score: 0, label: 'Fraca' });
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Reset state when user changes
  useEffect(() => {
    if (user) {
      setEditingUser({ ...user });
      // Derive full name from first/last
      const first = user.first_name || '';
      const last = user.last_name || '';
      setFullName((first + ' ' + last).trim());

      setSmtpConfig({
        smtp_email: user.smtp_email || '',
        smtp_senha: '',
        smtp_servidor: user.smtp_servidor || 'smtp.gmail.com',
        smtp_porta: user.smtp_porta || 587,
        smtp_tls: user.smtp_tls !== undefined ? user.smtp_tls : true,
      });

      // Reset password strength state
      setPasswordStrength({ score: 0, label: 'Fraca' });
      setPasswordErrors([]);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    }
  }, [user]);

  const handleNewPasswordChange = (value: string) => {
    if (!editingUser) return;
    setEditingUser({ ...editingUser, newPassword: value });

    if (value.length > 0) {
      validatePassword(value, editingUser.id)
        .then(result => {
          setPasswordStrength({ score: result.strength_score, label: result.strength_label });
          setPasswordErrors(result.errors);
        })
        .catch(() => {
          setPasswordStrength({ score: 0, label: 'Fraca' });
          setPasswordErrors(['Erro ao validar senha']);
        });
    } else {
      setPasswordStrength({ score: 0, label: 'Fraca' });
      setPasswordErrors([]);
    }
  };

  const handleSave = async () => {
    if (!editingUser) return;

    setSaving(true);
    
    // Split fullName back to first/last
    const nameParts = fullName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    const userToSave = {
      ...editingUser,
      first_name: firstName,
      last_name: lastName
    };

    const success = await onSave(userToSave, smtpConfig);
    setSaving(false);

    if (success) {
      onClose();
    }
  };

  if (!isOpen || !editingUser) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-5xl w-[95%] max-h-[95vh] overflow-y-auto shadow-2xl">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Editar Usuário
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column: User Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b pb-2">
              Dados Pessoais
            </h3>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                  Username (Não editável)
                </label>
                <input
                  type="text"
                  value={editingUser.username}
                  disabled
                  className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Digite o nome completo"
                  className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                  E-mail *
                </label>
                <input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  placeholder="Digite o e-mail"
                  className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                  Telefone
                </label>
                <input
                  type="tel"
                  value={editingUser.telefone || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, telefone: mascaraTelefone(e.target.value) })}
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                  className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                  Função no Sistema *
                </label>
                <select
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                  className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {ROLE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="edit-user-email-notif"
                  checked={editingUser.receber_email ?? false}
                  onChange={(e) => setEditingUser({ ...editingUser, receber_email: e.target.checked })}
                  className="w-5 h-5 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label htmlFor="edit-user-email-notif" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Receber notificações por e-mail
                </label>
              </div>

              {/* N116 (Alana): só MASTER vê e altera. Rótulo pela CONSEQUÊNCIA,
                  porque convive com o "Receber notificações" acima e os dois são
                  coisas diferentes — aquele é preferência do usuário, este
                  coloca o e-mail nos envios que vão para hospital e cliente. */}
              {/* ⛔ N166: o gate era só `isMaster`, que fala do usuário LOGADO
                  (useAuthStore) — não do usuário sendo cadastrado. Resultado: um
                  MASTER criando um auditor via o checkbox, e 13 AUDITORES
                  acabaram marcados. Agora depende do PERFIL ESCOLHIDO. A trava
                  de verdade é no backend (user_service/user_creation_service):
                  esconder na tela nunca protegeu nada. */}
              {isMaster && editingUser.role === 'MASTER' && (
                <div className="pt-2">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="edit-user-email-offline"
                      checked={editingUser.incluir_email_offline ?? false}
                      onChange={(e) => setEditingUser({ ...editingUser, incluir_email_offline: e.target.checked })}
                      className="w-5 h-5 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <label htmlFor="edit-user-email-offline" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Sempre incluir este e-mail nos envios do app offline
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-8">
                    O e-mail passa a aparecer já marcado na lista de
                    destinatários do aplicativo, junto dos contatos do hospital.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3 pt-4">
                <input
                  type="checkbox"
                  id="edit-user-ativo"
                  checked={editingUser.ativo}
                  onChange={(e) => setEditingUser({ ...editingUser, ativo: e.target.checked })}
                  className="w-5 h-5 text-purple-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:ring-2"
                />
                <label htmlFor="edit-user-ativo" className="text-base font-medium text-gray-700 dark:text-gray-300">
                  Usuário Ativo
                </label>
              </div>
            </div>
          </div>

          {/* Right Column: Password & SMTP */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b pb-2 mb-4 flex items-center gap-2">
                Alterar Senha (Opcional)
              </h3>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                    Nova Senha
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={editingUser.newPassword || ''}
                      onChange={(e) => handleNewPasswordChange(e.target.value)}
                      placeholder="Min. 8 caracteres"
                      className="w-full px-3.5 py-2.5 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>

                  {/* Password Strength Indicator */}
                  {editingUser.newPassword && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-600 dark:text-gray-400">Força da senha:</span>
                        <span style={{ color: getStrengthColor(passwordStrength.score) }} className="font-semibold">
                          {passwordStrength.label}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${passwordStrength.score}%`,
                            backgroundColor: getStrengthColor(passwordStrength.score)
                          }}
                        />
                      </div>
                      {passwordErrors.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {passwordErrors.map((error, idx) => (
                            <li key={idx} className="text-xs text-red-600 dark:text-red-400">• {error}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                    Confirmar Nova Senha
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={editingUser.confirmPassword || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, confirmPassword: e.target.value })}
                      placeholder="Confirme a nova senha"
                      className="w-full px-3.5 py-2.5 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>

              {editingUser.newPassword && editingUser.newPassword.length > 0 && editingUser.newPassword.length < 8 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  A senha deve ter no mínimo 8 caracteres
                </p>
              )}

              {editingUser.newPassword && editingUser.confirmPassword && editingUser.newPassword !== editingUser.confirmPassword && (
                <p className="text-xs text-red-500 mt-2">
                  As senhas não coincidem
                </p>
              )}
            </div>

            <div>
              <SMTPConfigSection
                config={smtpConfig}
                onChange={setSmtpConfig}
                hasPassword={editingUser.has_smtp_password || false}
                userId={editingUser.id}
                collapsible={true}
                defaultExpanded={true}
                showTestButton={true}
                title="Configuração SMTP (Envio de E-mails)"
                compact={true}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-8 justify-end border-t pt-4">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-semibold transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
